import marketsAllSpecials from "@faire/web-api/routes/markets/event_type/all-specials";
import insiderMembershipInfo from "@faire/web-api/routes/user/insider/info";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import qs from "query-string";
import React from "react";

import { IValueProp } from "@faire/retailer/features/FaireMarkets/ValueProps/AnimatedValueProps";
import { bpsToPercent } from "@faire/retailer/lib/bpsToDecimalAndPercent";
import { getTotalBrandCount } from "@faire/retailer/lib/getMarketingCount";
import { MarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

export const getIndependentBrands = (): IValueProp => ({
  title: (
    <StrictLocalMsg
      defaultMessage="Over {makerCount} independent brands at your fingertips"
      description={{
        text: "Value prop for the Faire Market event",
        img: "https://cdn.faire.com/fastly/e22550814034c49fa42fba6065dfcfa16047f951b59ba1a702477b55647e7d3f.png",
      }}
      values={DEPRECATED_backwardsCompatibleValues({
        makerCount: getTotalBrandCount(),
      })}
    />
  ),
  description: (
    <StrictLocalMsg defaultMessage="Faire Markets helps you build relationships with local brands across the globe. Access the world’s largest selection of independent brands in one place." />
  ),
});

export const getIndependentBrandsWithExamples = (): IValueProp => ({
  title: (
    <StrictLocalMsg
      defaultMessage="{makerCount} independent brands at your fingertips"
      values={DEPRECATED_backwardsCompatibleValues({
        makerCount: getTotalBrandCount(),
      })}
    />
  ),
  description: (
    <StrictLocalMsg defaultMessage="Faire Markets helps you build relationships with local brands across the globe. Access the world’s largest selection of independent brands in one place." />
  ),
});

export const getBestDeals = (): IValueProp => ({
  title: (
    <StrictLocalMsg
      defaultMessage="The best deals in wholesale—on us"
      description={{
        text: "There will be promotions on products that are discount matched by us",
      }}
    />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="Faire is offering our popular discount matching: get an extra {discountAmountNumber}% off all brand promotions during the market."
      values={{
        discountAmountNumber: bpsToPercent(
          MarketStore.get().matchedDiscountCapBps
        ),
      }}
    />
  ),
});

export const insiderUpsell = (): IValueProp => ({
  title: (
    <StrictLocalMsg defaultMessage="Plus, exclusive perks for Insider members" />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="Any retailer with an Insider membership also gets free shipping with thousands of participating brands. Plus, Insiders can shop the event a day early. <LinkFormatting>Learn more</LinkFormatting>"
      description={{
        text: "Upsell to encourage retailers to sign up for Insider membership",
        img: "https://cdn.faire.com/fastly/e22550814034c49fa42fba6065dfcfa16047f951b59ba1a702477b55647e7d3f.png",
      }}
      values={{
        marketEarlyAccessDate: MarketStore.get().formattedEarlyAccessStartDay,
        LinkFormatting: (chunks) => (
          <EmbeddedLink linkTo={insiderMembershipInfo()}>{chunks}</EmbeddedLink>
        ),
      }}
    />
  ),
});

export const saveTimeAndMoney = (): IValueProp => ({
  title: (
    <StrictLocalMsg
      defaultMessage="Save time and money to put back into your business"
      description={{
        text: "By attending the virtual event, you can save time and money",
      }}
    />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="No need to step away from your day-to-day business—skip the travel and connect with thousands of brands at {marketEventName}."
      values={{
        marketEventName: MarketStore.get().marketName,
      }}
    />
  ),
});

export const getEnrolledInsider = (): IValueProp => ({
  title: (
    <StrictLocalMsg defaultMessage="Exclusive perks for Insider members" />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="As an Insider member, you can start shopping the market on {insiderDate}—a day before everyone else."
      values={{
        insiderDate: MarketStore.get().formattedEarlyAccessStartDay,
      }}
    />
  ),
});

export const NOT_INSIDER: IValueProp = {
  title: (
    <StrictLocalMsg defaultMessage="Plus, plan your buying with new tools to help you shop" />
  ),
  description: (
    <StrictLocalMsg defaultMessage="Discover and add new brands you want to shop with interactive lists in our new event hub." />
  ),
};

export const getEnrolledInsiderBrowseAz = (): IValueProp => ({
  title: (
    <StrictLocalMsg defaultMessage="Exclusive perks for Insider members" />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="As an Insider member, you can start shopping the market on {formattedEarlyAccessStartDate}—a day before everyone else."
      values={DEPRECATED_backwardsCompatibleValues({
        formattedEarlyAccessStartDate:
          MarketStore.get().formattedEarlyAccessStartDay,
      })}
    />
  ),
  ctaLink: (
    <StrictLocalMsg
      defaultMessage="<LinkFormatting>Browse Insider brands</LinkFormatting>"
      values={{
        LinkFormatting: (chunks) => (
          <EmbeddedLink
            linkTo={marketsAllSpecialsWithFilter(
              "insider:insider_free_shipping"
            )}
          >
            {chunks}
          </EmbeddedLink>
        ),
      }}
      description={{
        text: "Retailer CTA to browse all participating brands with Insider filter turned on",
      }}
    />
  ),
});

export const NOT_INSIDER_BROWSE_AZ: IValueProp = {
  title: (
    <StrictLocalMsg defaultMessage="Plus, exclusive tools to help you plan your buying" />
  ),
  description: (
    <StrictLocalMsg defaultMessage="Discover and add new brands you want to shop with interactive lists in our new event hub." />
  ),
  ctaLink: (
    <StrictLocalMsg
      defaultMessage="<LinkFormatting>Browse participating brands</LinkFormatting>"
      values={{
        LinkFormatting: (chunks) => (
          <EmbeddedLink
            linkTo={marketsAllSpecials(MarketStore.get().currentEventType)}
          >
            {chunks}
          </EmbeddedLink>
        ),
      }}
      description={{
        text: "Retailer CTA to browse all participating brands",
      }}
    />
  ),
};

const marketsAllSpecialsWithFilter = (filterKey: string): string =>
  `${marketsAllSpecials(MarketStore.get().currentEventType)}?${qs.stringify({
    filters: encodeURI(filterKey),
  })}`;

export const getBestDealsFromHome = (): IValueProp => ({
  title: (
    <StrictLocalMsg
      defaultMessage="The best deals in wholesale—on us"
      description={{
        text: "There will be promotions on products that are discount matched by us",
      }}
    />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="Faire is offering our popular discount matching: get an extra {discountAmountNumber}% off all brand promotions during the market."
      values={{
        discountAmountNumber: bpsToPercent(
          MarketStore.get().matchedDiscountCapBps
        ),
      }}
    />
  ),
});

export const getLoggedInEligibleInsider = (): IValueProp => ({
  title: (
    <StrictLocalMsg defaultMessage="Plus, exclusive access for Insider members" />
  ),
  description: (
    <StrictLocalMsg
      defaultMessage="Members of Insider, our monthly membership program, get to shop the market a day early on {marketEarlyAccessDate}. <LinkFormatting>Learn More</LinkFormatting>"
      values={{
        marketEarlyAccessDate: MarketStore.get().formattedEarlyAccessStartDay,
        LinkFormatting: (chunks) => (
          <EmbeddedLink linkTo={insiderMembershipInfo()}>{chunks}</EmbeddedLink>
        ),
      }}
    />
  ),
});
